// The in-game header: the compact wordmark, an optional right slot, the sound toggle, and during the battle a segmented
// progress track underneath. The track is the battle's only count on screen (no "3/8" beside it), and it says
// "Question 3 of 8" to screen readers. `pulse` draws the wordmark's Pulse line: pass it only on a screen that shows no
// other Pulse (one Pulse per screen). The header is named `data-part="app-header"` so a look can restyle it (the bold
// and theatre looks make it a full-colour band, with the wordmark in white). The slot also carries the person icon (ProofMark) while
// the address that joined has played before and is not yet proved (9 Oct); it draws itself only then.
import type { ReactNode } from 'react';
import { useDesktop } from '@/lib/useDesktop';
import { FullScreenButton } from './FullScreenButton';
import { Wordmark } from '@/brand/Wordmark';
import { useLook } from '@/look/store';
import { MuteToggle } from './MuteToggle';
import { ProofMark } from './ProofMark';

export function AppHeader({ right, className = '', progress, pulse = false }: {
  right?: ReactNode; className?: string;
  /** index = questions finished or in play (0-based current index), total = questions in the battle. */
  progress?: { index: number; total: number };
  /** Draw the Pulse under "Rounds" (default: no). */
  pulse?: boolean;
}) {
  const { look } = useLook();
  const desktop = useDesktop();
  return (
    <header data-part="app-header" className={`flex flex-col gap-2.5 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <Wordmark variant="compact" pulse={pulse} tone={look === 'bold' || look === 'theatre' ? 'inverse' : 'ink'} className="text-[1.0625rem]" />
        <div data-part="app-header-slot" className="flex min-w-0 items-center gap-3 text-sm font-semibold text-ink-2">{right}<ProofMark />{desktop && <FullScreenButton />}<MuteToggle /></div>
      </div>
      {progress && progress.total > 0 && <ProgressTrack index={progress.index} total={progress.total} />}
    </header>
  );
}

/** Segments for each question: done (teal), current (orange), still to come (quiet). Square-cornered in every look. */
export function ProgressTrack({ index, total, className = '' }: { index: number; total: number; className?: string }) {
  const n = Math.min(total, 40);
  const now = Math.max(0, Math.min(total, index + 1));
  return (
    <div className={`flex gap-[3px] ${className}`} role="progressbar" aria-label="Battle progress" aria-valuemin={0} aria-valuemax={total} aria-valuenow={now}
      aria-valuetext={`Question ${Math.max(1, now)} of ${total}`}>
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className={`h-1.5 flex-1 rounded-xs ${i < index ? 'bg-brand' : i === index ? 'bg-cart' : 'bg-line'}`} />
      ))}
    </div>
  );
}
